/**
 * CORRECT MISTAKE MODAL
 * Reusable popup component for displaying and editing user's inserted data
 * Uses the same ColumnDefinition structure as the forms for consistent UI
 * @version 2.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Loader2, Calendar, Clock, Search, Filter, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ColumnDefinition } from '../../../universal_table/types/column.types';
import { formatDateDisplay } from '../../../universal_table/utils/cellFormatter';

const KEY_FIELD = '__rowKey';

type FilterState = {
  search: string;
  startDate: string;
  endDate: string;
  showOnlyEdited: boolean;
};

const DEFAULT_FILTERS: FilterState = Object.freeze({
  search: '',
  startDate: '',
  endDate: '',
  showOnlyEdited: false
});

const sanitizeRowForSave = (row: any) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const cloned = { ...row } as Record<string, unknown>;
  if (KEY_FIELD in cloned) {
    delete cloned[KEY_FIELD];
  }
  return cloned;
};

const createFallbackRowKey = (row: any, index: number): string => {
  if (row?.id != null) return `id:${row.id}`;
  if (row?.pk != null) return `pk:${row.pk}`;
  if (row?.uuid) return `uuid:${row.uuid}`;
  if (row?.entry_date) {
    const base = `date:${row.entry_date}`;
    if (row?.icr) return `${base}|icr:${row.icr}`;
    if (row?.inverter) return `${base}|inv:${row.inverter}`;
    if (row?.equipment) return `${base}|equipment:${row.equipment}`;
    return base;
  }
  if (row?.Date) {
    const base = `Date:${row.Date}`;
    if (row?.Feeder) return `${base}|feeder:${row.Feeder}`;
    return base;
  }
  if (row?.timestamp) return `ts:${row.timestamp}`;
  return `idx:${index}`;
};

const findMatchingRow = (row: any, candidates: any[]) => {
  if (!row || !candidates?.length) return undefined;

  const matchers: Array<(candidate: any) => boolean> = [
    candidate => row?.id != null && candidate?.id != null && row.id === candidate.id,
    candidate => row?.pk != null && candidate?.pk != null && row.pk === candidate.pk,
    candidate => row?.uuid && candidate?.uuid && row.uuid === candidate.uuid,
    candidate =>
      row?.entry_date && candidate?.entry_date &&
      row.entry_date === candidate.entry_date &&
      row?.icr && candidate?.icr && row.icr === candidate.icr &&
      row?.inverter && candidate?.inverter && row.inverter === candidate.inverter,
    candidate =>
      row?.entry_date && candidate?.entry_date &&
      row.entry_date === candidate.entry_date &&
      row?.equipment && candidate?.equipment && row.equipment === candidate.equipment,
    candidate =>
      row?.Date && candidate?.Date &&
      row.Date === candidate.Date &&
      row?.Feeder && candidate?.Feeder && row.Feeder === candidate.Feeder,
    candidate => row?.Date && candidate?.Date && row.Date === candidate.Date,
    candidate => row?.entry_date && candidate?.entry_date && row.entry_date === candidate.entry_date
  ];

  for (const matcher of matchers) {
    const found = candidates.find(matcher);
    if (found) {
      return found;
    }
  }
  return undefined;
};

const attachStableRowKey = (row: any, index: number, existingRows: any[]): any => {
  const fallbackKey = createFallbackRowKey(row, index);
  if (!existingRows.length) {
    return { ...row, [KEY_FIELD]: fallbackKey };
  }
  const match = findMatchingRow(row, existingRows);
  if (match) {
    const matchedKey = match?.[KEY_FIELD] ?? fallbackKey;
    return { ...row, [KEY_FIELD]: matchedKey };
  }
  return { ...row, [KEY_FIELD]: fallbackKey };
};

const normalizeDateForFilter = (value: any): string | null => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().split('T')[0];
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return null;
  }
  if (value != null) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
  }
  return null;
};

const valuesAreEqual = (a: any, b: any) => {
  if (a === b) return true;
  if (a == null && b == null) return true;

  if (typeof a === 'number' || typeof b === 'number') {
    const numA = Number(a ?? 0);
    const numB = Number(b ?? 0);
    if (Number.isNaN(numA) && Number.isNaN(numB)) return true;
    return numA === numB;
  }

  const stringA = String(a ?? '').trim();
  const stringB = String(b ?? '').trim();
  return stringA === stringB;
};

const rowMatchesOriginal = (row: any, originalRow: any, columns: ColumnDefinition[]) => {
  if (!row || !originalRow) return false;
  return columns.every(col => {
    if (!col?.key || typeof col.key !== 'string') return true;
    if (col.type === 'action' || col.key === '_delete') return true;
    return valuesAreEqual(row[col.key], originalRow[col.key]);
  });
};


export interface CorrectMistakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: any[];
  columns: ColumnDefinition[];
  onSave: (updatedData: any[]) => Promise<void>;
  isLoading?: boolean;
  siteCode?: string;
  // Lazy loading props
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  hasMore?: boolean;
}

export const CorrectMistakeModal: React.FC<CorrectMistakeModalProps> = ({
  isOpen,
  onClose,
  title,
  data,
  columns,
  onSave,
  isLoading = false,
  siteCode = 'axon',
  onLoadMore,
  isLoadingMore = false,
  hasMore = false
}) => {
  const [editedData, setEditedData] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });
  const [sidebarWidth, setSidebarWidth] = useState('176px');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<FilterState>({ ...DEFAULT_FILTERS });
  const [density, setDensity] = useState<'compact' | 'comfortable'>('compact');
  const [changedRows, setChangedRows] = useState<Set<string>>(new Set());

  const editableColumns = React.useMemo(() => {
    return columns.filter(col => {
      // Exclude action columns (like delete button)
      if (col.type === 'action' || col.key === '_delete') {
        return false;
      }
      // Exclude calculated columns
      if (col.calculated === true || col.type === 'calculated') {
        return false;
      }
      // Exclude readonly columns
      if (col.editable === false || col.type === 'readonly') {
        return false;
      }
      // Include all other columns (text, number, date, time, select, etc.)
      return true;
    });
  }, [columns]);

  const dateColumnKeys = React.useMemo(() => {
    return editableColumns
      .filter(col => {
        if (col.type === 'date') return true;
        if (typeof col.key === 'string') {
          return col.key.toLowerCase().includes('date');
        }
        return false;
      })
      .map(col => col.key);
  }, [editableColumns]);

  const totalRowCount = editedData.length;
  const unsavedRowCount = changedRows.size;

  const filteredRowIndices = React.useMemo(() => {
    if (!totalRowCount) return [] as number[];

    const hasSearch = Boolean(filters.search);
    const searchQuery = filters.search.toLowerCase();
    const startTimestamp = filters.startDate ? Date.parse(filters.startDate) : null;
    const endTimestamp = filters.endDate ? Date.parse(filters.endDate) : null;
    const requireEdited = filters.showOnlyEdited;

    return editedData.reduce<number[]>((acc, row, index) => {
      const rowKey = row?.[KEY_FIELD];

      if (requireEdited && (!rowKey || !changedRows.has(rowKey))) {
        return acc;
      }

      let matchesSearch = true;
      if (hasSearch) {
        matchesSearch = editableColumns.some(col => {
          if (!col?.key) return false;
          const value = row?.[col.key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchQuery);
        });
      }
      if (!matchesSearch) {
        return acc;
      }

      const needsDateComparison = Boolean(filters.startDate || filters.endDate) && dateColumnKeys.length > 0;
      if (needsDateComparison) {
        const matchesDate = dateColumnKeys.some(key => {
          const normalized = normalizeDateForFilter(row?.[key]);
          if (!normalized) return false;
          const valueTs = Date.parse(normalized);
          if (Number.isNaN(valueTs)) return false;
          if (startTimestamp !== null && valueTs < startTimestamp) return false;
          if (endTimestamp !== null && valueTs > endTimestamp) return false;
          return true;
        });

        if (!matchesDate) {
          return acc;
        }
      }

      acc.push(index);
      return acc;
    }, []);
  }, [editedData, editableColumns, filters, changedRows, dateColumnKeys, totalRowCount]);

  const visibleRowCount = filteredRowIndices.length;
  const hasFiltersApplied = Boolean(
    filters.search || filters.startDate || filters.endDate || filters.showOnlyEdited
  );

  const editedDataRef = useRef<any[]>([]);
  useEffect(() => {
    editedDataRef.current = editedData;
  }, [editedData]);

  const originalDataRef = useRef<any[]>([]);

  // Initialize edited data when modal opens or data changes
  useEffect(() => {
    if (!isOpen) {
      setEditedData([]);
      setFilters({ ...DEFAULT_FILTERS });
      setChangedRows(new Set());
      originalDataRef.current = [];
      setStatus({ type: null, message: '' });
      return;
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
setEditedData([]);
      originalDataRef.current = [];
      return;
    }
const previousRows = editedDataRef.current;
    const keyedIncoming = data.map((item, index) => attachStableRowKey(item, index, previousRows));

    const prevMap = new Map(
      (previousRows || []).map(row => [row?.[KEY_FIELD], row])
    );

    const mergedRows = keyedIncoming.map(row => {
      const existing = prevMap.get(row?.[KEY_FIELD]);
      if (existing) {
        return { ...row, ...existing };
      }
      return row;
    });

    originalDataRef.current = keyedIncoming.map(row => ({ ...row }));

    setEditedData(mergedRows);

    setChangedRows(prev => {
      if (!prev.size) return prev;
      const validKeys = new Set(mergedRows.map(row => row?.[KEY_FIELD]).filter(Boolean) as string[]);
      const next = new Set<string>();
      prev.forEach(key => {
        if (validKeys.has(key)) {
          next.add(key);
        }
      });
      return next;
    });
  }, [isOpen, data]);

  // Track sidebar width changes
  useEffect(() => {
    const updateSidebarWidth = () => {
      if (typeof window !== 'undefined') {
        const width = getComputedStyle(document.documentElement)
          .getPropertyValue('--sidebar-width')
          ?.trim() || '176px';
        setSidebarWidth(width);
      }
    };

    // Initial update
    updateSidebarWidth();

    // Listen for CSS variable changes (when sidebar is resized)
    const interval = setInterval(updateSidebarWidth, 100);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Scroll detection for lazy loading
  useEffect(() => {
    if (!isOpen || !onLoadMore || !hasMore || isLoadingMore) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger load more when user is within 200px of bottom
      const threshold = 200;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom < threshold && hasMore && !isLoadingMore) {
onLoadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isOpen, onLoadMore, hasMore, isLoadingMore]);

  const handleCellChange = (rowIndex: number, columnKey: string, value: any) => {
    setEditedData(prev => {
      if (!prev[rowIndex]) return prev;

      const updated = [...prev];
      const currentRow = updated[rowIndex];
      const rowKey = currentRow?.[KEY_FIELD];

      const nextRow = {
        ...currentRow,
        [columnKey]: value
      };

      const columnDef = columns.find(c => c.key === columnKey);
      if (columnDef?.dependencies) {
        columns.forEach(calcCol => {
          if (calcCol.calculateFn && calcCol.dependencies?.includes(columnKey)) {
            const calculatedValue = calcCol.calculateFn(nextRow, updated);
            nextRow[calcCol.key] = calculatedValue;
          }
        });
      }

      updated[rowIndex] = nextRow;

      if (rowKey) {
        const originalRow = originalDataRef.current.find(row => row?.[KEY_FIELD] === rowKey);
        const matchesOriginal = originalRow ? rowMatchesOriginal(nextRow, originalRow, editableColumns) : false;
        setChangedRows(prevChanged => {
          const next = new Set(prevChanged);
          if (matchesOriginal) {
            next.delete(rowKey);
          } else {
            next.add(rowKey);
          }
          return next;
        });
      }

      return updated;
    });
  };

  // Format time value for display
  const formatTimeValue = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    // If it's a number (minutes), convert to time string
    if (typeof value === 'number') {
      const hours = Math.floor(value / 60);
      const minutes = value % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    return String(value);
  };

  // Format date value for display
  const formatDateValue = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      // Try to parse and format
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    }
    return String(value);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatus({ type: null, message: '' });

    try {
      const sanitizedData = editedData.map(sanitizeRowForSave);
      await onSave(sanitizedData);
      setStatus({ type: 'success', message: '✅ Data saved successfully!' });
      originalDataRef.current = editedData.map(row => ({ ...row }));
      setChangedRows(new Set());
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      console.error('âŒ Save error in CorrectMistakeModal:', errorMessage);
      setStatus({ type: 'error', message: `âŒ Failed to save: ${errorMessage}` });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const handleResetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
  };

  const handleToggleEditedFilter = () => {
    setFilters(prev => ({
      ...prev,
      showOnlyEdited: !prev.showOnlyEdited
    }));
  };

  const handleRestoreRow = (rowKey?: string) => {
    if (!rowKey) return;
    const originalRow = originalDataRef.current.find(row => row?.[KEY_FIELD] === rowKey);
    if (!originalRow) return;

    setEditedData(prev => prev.map(row => {
      if (row?.[KEY_FIELD] === rowKey) {
        return { ...originalRow };
      }
      return row;
    }));

    setChangedRows(prev => {
      const next = new Set(prev);
      next.delete(rowKey);
      return next;
    });
  };

  const handleResetAllChanges = () => {
    if (!originalDataRef.current.length) return;
    const restored = originalDataRef.current.map(row => ({ ...row }));
    setEditedData(restored);
    setChangedRows(new Set());
    setStatus({ type: null, message: '' });
  };

  const dateFilterDisabled = dateColumnKeys.length === 0;
  const densityLabel = density === 'compact' ? 'Compact view' : 'Comfort view';

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        style={{
          marginLeft: sidebarWidth,
          left: 0,
          right: 0
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-2xl w-[90vw] h-[85vh] max-w-7xl flex flex-col correct-mistake-modal"
          style={{
            margin: '0 auto',
            maxWidth: `calc(90vw - ${sidebarWidth})`
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-[#0084CF] to-[#0066A3] text-white rounded-t-lg">
            <h2 className="text-lg font-bold" style={{ color: '#ffffff' }}>{title}</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Status Banner */}
          {status.type && (
            <div className={`px-6 py-2 ${status.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
              <p className="text-sm font-medium">{status.message}</p>
            </div>
          )}

          {/* Content */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-auto p-6 space-y-5 correct-mistake-modal__scroll"
            style={{ overflowX: 'hidden', overflowY: 'auto', scrollbarGutter: 'stable both-edges' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-[#0084CF]" size={32} />
                <span className="ml-3 text-gray-600">Loading your data...</span>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {/* Search Input */}
                  <div className="relative flex items-center flex-shrink-0">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      placeholder="Search within records"
                      className="pl-7 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] w-[180px]"
                    />
                  </div>

                  {/* Date Range Filter */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} className="text-gray-400 flex-shrink-0" />
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] w-[110px]"
                        disabled={dateFilterDisabled}
                      />
                    </div>
                    <span className="text-gray-400 flex-shrink-0">to</span>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] w-[110px]"
                      disabled={dateFilterDisabled}
                    />
                  </div>

                  {/* Filter Toggle Button */}
                  <button
                    onClick={handleToggleEditedFilter}
                    className={`correct-mistake-modal__toolbar-button inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs transition-colors flex-shrink-0 whitespace-nowrap ${filters.showOnlyEdited
                      ? 'bg-[#0084CF]/10 border-[#0084CF] text-[#005a8a]'
                      : 'border-gray-300 text-gray-600 hover:border-[#0084CF]/60'
                      }`}
                  >
                    <Filter size={12} />
                    {filters.showOnlyEdited ? 'Showing edited only' : 'All rows'}
                  </button>

                  {/* Reset Filters Button */}
                  {hasFiltersApplied && (
                    <button
                      onClick={handleResetFilters}
                      className="correct-mistake-modal__toolbar-button inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:border-[#0084CF]/60 flex-shrink-0 whitespace-nowrap"
                    >
                      <RotateCcw size={12} />
                      Reset filters
                    </button>
                  )}

                  {/* Right-aligned Action Buttons */}
                  <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setDensity(prev => (prev === 'compact' ? 'comfortable' : 'compact'))}
                      className="correct-mistake-modal__toolbar-button inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:border-[#0084CF]/60 whitespace-nowrap"
                      title={densityLabel}
                    >
                      <SlidersHorizontal size={12} />
                      {density === 'compact' ? 'Compact' : 'Comfort'}
                    </button>
                    <button
                      onClick={handleResetAllChanges}
                      className="correct-mistake-modal__toolbar-button inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:border-[#F97316]/60 whitespace-nowrap"
                      disabled={!unsavedRowCount}
                    >
                      <RotateCcw size={12} />
                      Reset changes
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span>Total records: <strong>{totalRowCount}</strong></span>
                  <span>Visible: <strong>{visibleRowCount}</strong></span>
                  {unsavedRowCount > 0 && (
                    <span className="text-[#005a8a] font-medium">
                      Unsaved changes: {unsavedRowCount}
                    </span>
                  )}
                  {hasMore && (
                    <span className="text-gray-400">Scroll to the bottom to load more</span>
                  )}
                </div>

                {(!editedData || editedData.length === 0) ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-gray-500 text-lg mb-2">No records available.</p>
                      <p className="text-gray-400 text-sm">Try adjusting filters or add new entries from the sheet.</p>
                    </div>
                  </div>
                ) : (
                  <div className="correct-mistake-modal__table-container overflow-x-auto" style={{ width: '100%', overflowX: 'scroll', overflowY: 'visible', WebkitOverflowScrolling: 'touch' }}>
                    <style>{`
                  .correct-mistake-modal .date-column-header-text {
                    color: #000000 !important;
                    font-weight: 700 !important;
                    font-size: 11px !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                  }
                  .correct-mistake-modal th .date-column-header-text {
                    color: #000000 !important;
                    font-weight: 700 !important;
                  }
                  /* Ensure horizontal scrollbar is visible */
                  .correct-mistake-modal .correct-mistake-modal__table-container {
                    scrollbar-width: thin;
                    scrollbar-color: #cbd5e1 #f1f5f9;
                    padding-bottom: 12px;
                    margin-bottom: 8px;
                  }
                  .correct-mistake-modal .correct-mistake-modal__table-container::-webkit-scrollbar {
                    height: 12px;
                  }
                  .correct-mistake-modal .correct-mistake-modal__table-container::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 6px;
                  }
                  .correct-mistake-modal .correct-mistake-modal__table-container::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 6px;
                  }
                  .correct-mistake-modal .correct-mistake-modal__table-container::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                  }
                  .correct-mistake-modal .correct-mistake-modal__toolbar-button {
                    transition: all 0.2s ease;
                  }
                  .correct-mistake-modal .correct-mistake-modal__toolbar-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                  }
                `}</style>
                    <table className="border-collapse" style={{ fontSize: '11px', width: 'auto', minWidth: '100%', tableLayout: 'auto', borderSpacing: 0 }}>
                      <thead>
                        <tr className="bg-gray-200">
                          {editableColumns.map((col, colIndex) => {
                            // Special handling for Date column - always show "Date"
                            let displayLabel = col.abbreviation || col.label || col.key || 'Column';
                            if (col.type === 'date' || col.key === 'date' || col.key === 'Date' || col.key === 'entry_date') {
                              displayLabel = 'Date';
                            }
                            const isSticky = col.sticky;
                            const isLastColumn = colIndex === editableColumns.length - 1;
                            // Calculate minimum width based on label length to prevent truncation
                            const labelLength = displayLabel ? displayLabel.length : 0;
                            const calculatedMinWidth = Math.max(
                              col.minWidth || 120,
                              labelLength > 0 ? labelLength * 7 + 24 : 120 // ~7px per character + padding, minimum 120px
                            );
                            return (
                              <th
                                key={col.key}
                                className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${isSticky ? 'sticky left-0 z-20 bg-gray-200' : ''
                                  }`}
                                style={{
                                  minWidth: `${calculatedMinWidth}px`,
                                  width: isLastColumn ? 'auto' : (col.minWidth ? `${Math.max(col.minWidth, 120)}px` : 'auto'),
                                  textAlign: col.textAlign || 'left',
                                  whiteSpace: 'nowrap',
                                  overflow: isLastColumn ? 'visible' : 'hidden',
                                  textOverflow: isLastColumn ? 'clip' : 'ellipsis',
                                  border: '1px solid #d1d5db',
                                  borderBottom: '2px solid #9ca3af',
                                  backgroundColor: '#f3f4f6',
                                  position: isSticky ? 'sticky' : 'relative',
                                  left: isSticky ? 0 : 'auto',
                                  zIndex: isSticky ? 20 : 'auto',
                                  boxShadow: isSticky ? '2px 0 4px rgba(0, 0, 0, 0.1)' : 'none',
                                  borderRight: isSticky ? '2px solid #9ca3af' : '1px solid #d1d5db',
                                  paddingRight: isLastColumn ? '12px' : '12px'
                                }}
                                title={col.label !== displayLabel ? col.label : col.description}
                              >
                                <span
                                  className="date-column-header-text"
                                  style={{
                                    display: 'block',
                                    color: '#000000',
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    lineHeight: '1.4',
                                    visibility: 'visible',
                                    opacity: 1,
                                    width: '100%',
                                    textAlign: col.textAlign || 'left'
                                  }}
                                >
                                  {displayLabel}
                                </span>
                              </th>
                            );
                          })}
                          <th
                            className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider sticky right-0 z-30 bg-gray-200"
                            style={{
                              minWidth: '120px',
                              border: '1px solid #d1d5db',
                              borderBottom: '2px solid #9ca3af',
                              borderLeft: '2px solid #9ca3af',
                              textAlign: 'right'
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRowCount > 0 ? filteredRowIndices.map((visibleIndex, position) => {
                          const row = editedData[visibleIndex];
                          const rowKey = row?.[KEY_FIELD] as string | undefined;
                          const isRowChanged = !!rowKey && changedRows.has(rowKey);
                          const firstVisibleRow = position === 0;
                          const dateInputBaseId = `modal-date-${rowKey || visibleIndex}`;
                          return (
                            <tr
                              key={rowKey || `row-${visibleIndex}`}
                              className={`border-b transition-colors ${isRowChanged ? 'bg-[#F2F8FC]/60 hover:bg-[#E3F0F9]/60' : 'bg-white hover:bg-gray-50'}`}
                              style={{ height: 'auto', minHeight: '32px' }}
                            >
                              {editableColumns.map((col) => {
                                const value = row[col.key];
                                const isEditable = col.editable !== false && !col.calculated && col.type !== 'readonly';
                                const isSticky = col.sticky;
                                const cellType = col.type || 'text';

                                // Calculate value if it's a calculated field
                                let displayValue = value;
                                if (col.calculateFn && col.calculated) {
                                  displayValue = col.calculateFn(row, editedData);
                                }

                                // Handle null/undefined values - convert to empty string for display
                                if (displayValue === null || displayValue === undefined) {
                                  displayValue = '';
                                }

                                return (
                                  <td
                                    key={col.key}
                                    className={`px-3 ${density === 'compact' ? 'py-1.5' : 'py-2.5'} text-xs ${isSticky ? 'sticky left-0 z-10 bg-white' : ''
                                      }`}
                                    style={{
                                      minWidth: col.minWidth ? `${Math.max(col.minWidth, 120)}px` : '120px',
                                      width: col.minWidth ? `${Math.max(col.minWidth, 120)}px` : 'auto',
                                      textAlign: col.textAlign || (cellType === 'number' ? 'center' : 'left'),
                                      whiteSpace: 'nowrap',
                                      overflow: 'visible',
                                      verticalAlign: 'middle',
                                      height: 'auto',
                                      border: '1px solid #d1d5db',
                                      borderTop: firstVisibleRow ? 'none' : '1px solid #d1d5db',
                                      position: isSticky ? 'sticky' : 'relative',
                                      left: isSticky ? 0 : 'auto',
                                      zIndex: isSticky ? 10 : 'auto',
                                      backgroundColor: isSticky ? '#ffffff' : (isRowChanged ? '#F2F8FC' : 'transparent'),
                                      boxShadow: isSticky ? '2px 0 4px rgba(0, 0, 0, 0.1)' : 'none',
                                      borderRight: isSticky ? '2px solid #9ca3af' : '1px solid #d1d5db'
                                    }}
                                  >
                                    {isEditable ? (
                                      <div className="flex items-center gap-1 min-w-0 w-full">
                                        {cellType === 'select' && col.options ? (
                                          <>
                                            <select
                                              value={displayValue || ''}
                                              onChange={(e) => handleCellChange(visibleIndex, col.key, e.target.value)}
                                              className="flex-1 px-2 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#0084CF] bg-white"
                                              style={{ fontSize: '11px', height: '24px' }}
                                            >
                                              <option value="">-</option>
                                              {col.options.map((opt) => {
                                                const optValue = typeof opt === 'string' ? opt : opt.value;
                                                const optLabel = typeof opt === 'string' ? opt : opt.label;
                                                return (
                                                  <option key={optValue} value={optValue}>{optLabel}</option>
                                                );
                                              })}
                                            </select>
                                            {col.suffix && (
                                              <span className="text-xs text-gray-500">{col.suffix}</span>
                                            )}
                                          </>
                                        ) : cellType === 'number' ? (
                                          <>
                                            <input
                                              type="number"
                                              value={displayValue || ''}
                                              onChange={(e) => handleCellChange(visibleIndex, col.key, e.target.value ? parseFloat(e.target.value) : null)}
                                              className="flex-1 px-2 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#0084CF] text-center"
                                              style={{ fontSize: '11px', height: '24px', minWidth: '80px' }}
                                              step="0.01"
                                              placeholder={col.placeholder}
                                            />
                                            {col.suffix && (
                                              <span className="text-xs text-gray-500 whitespace-nowrap">{col.suffix}</span>
                                            )}
                                          </>
                                        ) : cellType === 'date' ? (
                                          <div className="flex items-center gap-1.5 w-full">
                                            <span className="text-xs font-medium text-gray-800 whitespace-nowrap flex-1 min-w-0">
                                              {displayValue ? formatDateDisplay(displayValue) : '-'}
                                            </span>
                                            <input
                                              type="date"
                                              id={`${dateInputBaseId}-${String(col.key).replace(/\s+/g, '-')}`}
                                              value={formatDateValue(displayValue)}
                                              onChange={(e) => handleCellChange(visibleIndex, col.key, e.target.value)}
                                              style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px' }}
                                              tabIndex={-1}
                                            />
                                            <span title="Click to change date">
                                              <Calendar
                                                size={14}
                                                className="text-[#0084CF] cursor-pointer flex-shrink-0 hover:text-[#1C4D88] transition-colors"
                                                onClick={() => {
                                                  const inputId = `${dateInputBaseId}-${String(col.key).replace(/\s+/g, '-')}`;
                                                  const input = document.getElementById(inputId) as HTMLInputElement;
                                                  if (input?.showPicker) {
                                                    input.showPicker();
                                                  }
                                                }}
                                              />
                                            </span>
                                          </div>
                                        ) : cellType === 'time' ? (
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="time"
                                              value={formatTimeValue(displayValue)}
                                              onChange={(e) => handleCellChange(visibleIndex, col.key, e.target.value)}
                                              className="flex-1 px-2 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#0084CF]"
                                              style={{ fontSize: '11px', height: '24px' }}
                                            />
                                            <Clock size={12} className="text-[#0084CF] cursor-pointer" />
                                          </div>
                                        ) : (
                                          <input
                                            type="text"
                                            value={displayValue || ''}
                                            onChange={(e) => handleCellChange(visibleIndex, col.key, e.target.value)}
                                            className="w-full px-2 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#0084CF]"
                                            style={{ fontSize: '11px', height: '24px', minWidth: '100px' }}
                                            placeholder={col.placeholder}
                                          />
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 min-w-0">
                                        <span className="text-gray-700 text-xs whitespace-nowrap overflow-visible">
                                          {col.format
                                            ? col.format(displayValue, row)
                                            : cellType === 'date' && displayValue
                                              ? formatDateDisplay(displayValue)
                                              : (displayValue ?? '-')}
                                        </span>
                                        {col.suffix && displayValue && (
                                          <span className="text-xs text-gray-500 whitespace-nowrap">{col.suffix}</span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                              <td
                                className={`px-3 ${density === 'compact' ? 'py-1.5' : 'py-2.5'} text-xs sticky right-0 z-20`}
                                style={{
                                  minWidth: '120px',
                                  textAlign: 'right',
                                  backgroundColor: isRowChanged ? '#C2DFF1' : '#f8fafc',
                                  border: '1px solid #d1d5db',
                                  borderLeft: '2px solid #9ca3af'
                                }}
                              >
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleRestoreRow(rowKey)}
                                    className="px-2 py-1 text-[11px] rounded border border-gray-300 text-gray-600 hover:border-[#0084CF] hover:text-[#005a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!isRowChanged}
                                  >
                                    Restore
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr>
                            <td colSpan={editableColumns.length + 1} className="text-center py-8 text-gray-500">
                              No records match the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {/* Loading more indicator */}
                    {isLoadingMore && (
                      <div className="flex items-center justify-center py-4 border-t">
                        <Loader2 className="animate-spin text-[#0084CF] mr-2" size={16} />
                        <span className="text-sm text-gray-600">Loading more records...</span>
                      </div>
                    )}
                    {!hasMore && editedData.length > 0 && (
                      <div className="flex items-center justify-center py-2 text-xs text-gray-500">
                        No more records to load
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
            <div className="flex flex-col">
              <p className="text-sm text-gray-600">
                Showing {visibleRowCount} of {totalRowCount} record{totalRowCount === 1 ? '' : 's'}
                {unsavedRowCount > 0 && (
                  <span className="ml-2 text-[#ea580c] font-medium">• {unsavedRowCount} pending change{unsavedRowCount === 1 ? '' : 's'}</span>
                )}
              </p>
              <p className="text-xs text-[#0066A3] mt-1">
                âœï¸ Update the values inline, then press "Save Changes" to publish your edits
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isLoading || editedData.length === 0 || unsavedRowCount === 0}
                title={unsavedRowCount === 0 ? 'No changes to save yet' : undefined}
                className="px-4 py-2 text-sm font-medium text-white bg-[#0084CF] rounded-lg hover:bg-[#1C4D88] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};



